import { experience } from '@logto/schemas';
import {
  isSignUpEnabled as isSignUpEnabledHelper,
  resolveExperienceConfig,
  type ResolvedExperienceProfile,
} from '@quickguidehealth/experience-config';
import { loadRuntimeExperienceConfig } from '@quickguidehealth/experience-config/web';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { searchKeys } from '@/shared/utils/search-parameters';

import { defaultExperienceConfig } from './default-experience-config';

type QgExperienceContextValue = {
  /**
   * The resolved per-app profile for this session, or `undefined` while the
   * config is loading or if it failed to load. Consumers treat `undefined` as
   * "pass through" — i.e. fall back to Logto's defaults — so sign-in never breaks
   * on a config failure.
   */
  resolved?: ResolvedExperienceProfile;
};

const QgExperienceContext = createContext<QgExperienceContextValue>({});

/** Read `app_id` the way Logto stores it: sessionStorage first, then the URL. */
const readAppId = (): string | undefined => {
  const fromSession = sessionStorage.getItem(searchKeys.appId);
  if (fromSession) {
    return fromSession;
  }
  return new URLSearchParams(window.location.search).get(searchKeys.appId) ?? undefined;
};

/**
 * Override Logto's brand color CSS variable from the resolved profile, picking
 * the dark/light variant from the `data-theme` already set on <html>. Extends
 * the SSR `--color-brand-default` injection in `index.html`.
 */
const applyBranding = (resolved: ResolvedExperienceProfile): void => {
  const { branding } = resolved;
  if (!branding) {
    return;
  }
  const isDark = document.documentElement.dataset.theme === 'dark';
  const color = isDark
    ? (branding.darkPrimaryColor ?? branding.primaryColor)
    : branding.primaryColor;
  if (color) {
    document.body.style.setProperty('--color-brand-default', color);
  }
};

/**
 * Loads the runtime experience config, resolves it for the current `app_id` +
 * hostname, and exposes the resolved profile. Fails open: on any error it leaves
 * `resolved` undefined so the UI falls back to Logto's stock behavior.
 */
export const QgExperienceProvider = ({ children }: { readonly children: ReactNode }) => {
  const [resolved, setResolved] = useState<ResolvedExperienceProfile | undefined>();

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const document_ = await loadRuntimeExperienceConfig({ fallback: defaultExperienceConfig });
        if (controller.signal.aborted) {
          return;
        }
        const next = resolveExperienceConfig({
          appId: readAppId(),
          hostname: window.location.hostname,
          document: document_,
        });
        setResolved(next);
        applyBranding(next);
      } catch (error) {
        // Never block sign-in on config — fall back to Logto defaults.
        console.error(
          '[qg-experience] failed to load experience config; using Logto defaults',
          error
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const value = useMemo<QgExperienceContextValue>(() => ({ resolved }), [resolved]);

  return <QgExperienceContext.Provider value={value}>{children}</QgExperienceContext.Provider>;
};

/** Raw context access (for `useSieMethods`, which derives the connector lists). */
export const useQgExperienceContext = (): QgExperienceContextValue =>
  useContext(QgExperienceContext);

/** Convenience accessor for the resolved profile + sign-up gate (fails open to enabled). */
export const useQgExperience = (): {
  resolved?: ResolvedExperienceProfile;
  signUpEnabled: boolean;
} => {
  const { resolved } = useContext(QgExperienceContext);
  return {
    resolved,
    signUpEnabled: resolved ? isSignUpEnabledHelper(resolved) : true,
  };
};

/**
 * Layout-route guard that "does not serve" the register subtree when the resolved
 * profile disables sign-up: it redirects to sign-in. Fails open (renders the
 * route) while the config is still loading or if it failed.
 */
export const QgRegisterGuard = () => {
  const { signUpEnabled } = useQgExperience();
  if (!signUpEnabled) {
    return <Navigate replace to={`/${experience.routes.signIn}`} />;
  }
  return <Outlet />;
};
